import {useQuery} from 'react-query'
import {deleteConductor, fetchConductorList} from './api'
import {PageTitle} from '../../../_metronic/layout/core'
import {Error500} from '../../modules/errors/components/Error500'
import CRUDTable from '../../../components/table/CRUDTable'
import ConductorForm from './Form'
import toast, {Toaster} from 'react-hot-toast'
import DeleteButton from '../../../components/DeleteButton'
import ModalWithForm from '../../../components/ModalWithForm'
import EditBtn from '../../../components/EditBtn'
import {Button} from 'react-bootstrap'
import viewProps from '../viewProps'
import {Processing} from '../../../components/Loaders'
import {Helmet} from 'react-helmet'
import {APIResponse} from '../../../types/type'
import {BusAttendeeBase} from './type'
import ActivePill from '../../../components/ActivePill'
import InactivePill from '../../../components/InactivePill'
import DeleteLabel from '../../../components/DeleteLabel'
import DeleteConfirmationModal from '../../../components/DeleteWarning'
import EditLabel from '../../../components/EditLabel'
import PortalDropdown from '../../../components/PortalDropdown'
import TopBarProgress from 'react-topbar-progress-indicator'
import NFC from './NFC'
import clsx from 'clsx'
import WithModal from '../../../components/WithModal'
import ViewAll from '../../../components/ViewAll'
import MoreInfoLabel from '../../../components/MoreInfoLabel'

const conductorBreadcrumbs = [
  {
    title: 'School Management',
    path: 'school-management/conductors',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

function BusAttendee() {
  const {data, isError, isFetching, isLoading, refetch} = useQuery<APIResponse<BusAttendeeBase[]>>(
    'conductors',
    fetchConductorList,
    {
      refetchOnWindowFocus: false,
    }
  )

  if (isLoading) {
    return <Processing />
  }
  const cellModifier = {
    conductor_status: ({value}: {value: string}) => {
      switch (Number(value)) {
        case 1:
          return <ActivePill />
        case 0:
          return <InactivePill />
        default:
          return ''
      }
    },
    mobile_display: ({row}: {row: {original: BusAttendeeBase}}) => {
  const { country_code, conductor_mobile } = row.original

  // ✅ safely default to empty string if null/undefined
  const cc = (country_code ?? '').toString().replace(/^\+/, '')
  let mobile = (conductor_mobile ?? '').toString().replace(/^\+/, '')

  if (cc && mobile.startsWith(cc)) {
    mobile = mobile.slice(cc.length)
  }

  return <span>{cc ? `${cc} ${mobile}` : mobile}</span>
},
    more: ({row}: {row: {original: BusAttendeeBase}}) => {
      const orderedFields = [
        //...Object.entries(viewProps.BusAttendees.primaryFieldsToLabelMap),
        ...Object.entries(viewProps.BusAttendees.secondaryFieldsToMap),
      ]
      return (
        <WithModal
          renderModalBody={() => (
            <ViewAll data={row.original} cellModifier={cellModifier} fieldOrder={orderedFields} />
          )}
          modalTitle={
            <span>
              Details of <span className="text-primary">{row.original.conductor_name}</span>
            </span>
          }
          modalAttrs={{backdrop: true, size: 'lg'}}
        >
          <MoreInfoLabel />
        </WithModal>
      )
    },
    Edit: ({row}: {row: {original: BusAttendeeBase}}) => (
      <ModalWithForm
        modalTitle={'Update Bus Attendee'}
        initialValues={row.original}
        onSubmit={refetch}
        Form={ConductorForm}
      >
        <EditBtn />
      </ModalWithForm>
    ),
    Delete: ({row}: {row: {original: BusAttendeeBase}}) => (
      <DeleteButton
        onDelete={async () => {
          const toastId = toast.loading('Deleting Please wait...')
          try {
            const response = await deleteConductor(row.original.conductor_id)
            if (response?.status === false && response?.message) {
              toast.error(response.message, {id: toastId})
              return null
            }
            await refetch()
            toast.success('Deleted Successfully', {id: toastId})
          } catch (error) {
            toast.error('Something went wrong, please try again later', {id: toastId})
          }
        }}
      />
    ),
    Actions: ({row}: {row: {original: BusAttendeeBase}}) => {
      return (
        <PortalDropdown triggerProps={{className: 'btn btn-secondary px-0 py-1'}}>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={
                <span>
                  RFID of <span className="text-primary">{row.original.conductor_name}</span>
                </span>
              }
              initialValues={row.original}
              Form={NFC}
              modalAttrs={{size: 'sm'}}
              onSubmit={refetch}
              triggerClassName="d-block"
            >
              <div className="px-3 py-2">
                <i
                  className={clsx(
                    'fa-brands fa-nfc-symbol fs-6',
                    row.original.nfc_code ? 'link-info' : 'link-secondary'
                  )}
                />
                <span className="ms-2">RFID</span>
              </div>
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={'Update Bus Attendee'}
              initialValues={row.original}
              onSubmit={refetch}
              Form={ConductorForm}
              triggerClassName="d-block"
              modalAttrs={{size: 'lg'}}
            >
              <EditLabel />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <DeleteConfirmationModal
              triggerClassName="d-block"
              onDelete={async () => {
                const toastId = toast.loading('Deleting Please wait...')
                try {
                  const response = await deleteConductor(row.original.conductor_id)
                  if (response?.status === false && response?.message) {
                    toast.error(response.message, {id: toastId})
                    return null
                  }
                  await refetch()
                  toast.success('Deleted Successfully', {id: toastId})
                } catch (error) {
                  toast.error('Something went wrong, please try again later', {id: toastId})
                }
              }}
            >
              <DeleteLabel />
            </DeleteConfirmationModal>
          </span>
        </PortalDropdown>
      )
    },
  }

  const processedData = (data?.data || []).map((item) => {
  const cc = (item.country_code ?? '').toString().replace(/^\+/, '')
  let mobile = (item.conductor_mobile ?? '').toString().replace(/^\+/, '')
  if (cc && mobile.startsWith(cc)) mobile = mobile.slice(cc.length)
  const display = cc ? `+${cc} ${mobile}` : mobile

  return {
    ...item,
    mobile_display: display,
    searchable_mobile: `${cc}${mobile}`.replace(/\s+/g, ''), // ✅ hidden searchable field
  }
})

  return (
    <>
      <Helmet>
        <title>TrackNAB | Bus Attendees</title>
      </Helmet>
      <PageTitle breadcrumbs={conductorBreadcrumbs}>Bus Attendees</PageTitle>
      {isError ? (
        <div className="text-center">
          <Error500 />
        </div>
      ) : (
        <CRUDTable
          data={processedData || []}
          {...viewProps.BusAttendees}
          fieldToLabelMap={viewProps.BusAttendees.primaryFieldsToLabelMap}
          Form={ConductorForm}
          formSize={{size: 'md'}}
          cellModifier={cellModifier}
          nonSortableColumns={['Actions']}
          headerExtras={
            <ModalWithForm
              modalTitle={'Add Bus Attendee'}
              triggerClassName="ms-auto"
              onSubmit={refetch}
              Form={ConductorForm}
              modalAttrs={{size: 'lg'}}
            >
              <Button size="sm">Add Bus Attendee</Button>
            </ModalWithForm>
          }
        />
      )}
      {isFetching && <TopBarProgress />}
      <Toaster />
    </>
  )
}

export default BusAttendee
